/**
 * E06-F01-T13 — pure rules for the Intake "Link to project" autocomplete.
 *
 * The field used to be a flat `<select>` over every project (most titled almost
 * the same, e.g. "New Position") — unusable on a long list. It is now a search
 * box filtering by CLIENT name, ROLE name and project ID. All matching / labeling
 * lives here, DOM-free, so it is unit-tested without React.
 */
import type { ProjectListItemDTO } from '@eer/shared';

/** The fields of a project row the picker reads. */
export type ProjectPickOption = Pick<ProjectListItemDTO, 'id' | 'client_name' | 'role_name'>;

/** Max rows rendered in the dropdown (refine the query to see the rest). */
export const PROJECT_PICKER_LIMIT = 50;

/** Display label: "Client — Role · #id" (missing parts dropped). */
export function projectLabel(p: ProjectPickOption): string {
  const client = p.client_name?.trim();
  const role = p.role_name?.trim();
  const head = [client, role].filter((s): s is string => Boolean(s)).join(' — ');
  return head ? `${head} · #${p.id}` : `Project #${p.id}`;
}

/** Lower-cased search haystack: id, "#id", client, role. */
function haystack(p: ProjectPickOption): string {
  return `${p.id} #${p.id} ${p.client_name ?? ''} ${p.role_name ?? ''}`.toLowerCase();
}

/**
 * Filter projects by a free-text query. Every whitespace-separated token must
 * occur (case-insensitive substring) in the client name, role name or ID — so
 * "acme cfo" narrows to Acme's CFO search. A token that is a bare number or
 * "#number" equal to a project id ranks that project first. An empty query keeps
 * the input order (the list is newest-updated first). Result is capped at
 * `limit`; `total` reports the un-capped match count.
 */
export function filterProjectOptions<T extends ProjectPickOption>(
  projects: readonly T[],
  query: string,
  limit: number = PROJECT_PICKER_LIMIT,
): { readonly items: T[]; readonly total: number } {
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) {
    return { items: projects.slice(0, limit), total: projects.length };
  }
  const idTokens = new Set(
    tokens
      .map((t) => t.replace(/^#/, ''))
      .filter((t) => /^\d+$/.test(t))
      .map(Number),
  );
  const matched = projects.filter((p) => {
    const hay = haystack(p);
    return tokens.every((t) => hay.includes(t));
  });
  const exact = matched.filter((p) => idTokens.has(p.id));
  const rest = matched.filter((p) => !idTokens.has(p.id));
  const ordered = [...exact, ...rest];
  return { items: ordered.slice(0, limit), total: ordered.length };
}
